import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, UserPlus, Trash2, RotateCcw, ShieldCheck, MonitorSmartphone, Copy, Clock, AlertTriangle, Database, Eraser } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminSettings() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [newAdvisor, setNewAdvisor] = useState({ user_id: "", full_name: "" });
  const [loading, setLoading] = useState(true);
  const [pendingOtp, setPendingOtp] = useState(null);

  const loadAdvisors = async () => {
    setLoading(true);
    try {
      const r = await api.get("/advisors");
      setAdvisors(r.data || []);
    } catch { /* not owner */ }
    finally { setLoading(false); }
  };
  const loadPendingOtp = async () => {
    try {
      const r = await api.get("/advisor-auth/latest-otp");
      setPendingOtp(r.data?.otp || null);
    } catch { /* noop */ }
  };
  useEffect(() => {
    if (isOwner) { loadAdvisors(); loadPendingOtp(); }
    const iv = setInterval(() => { if (isOwner) loadPendingOtp(); }, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [isOwner]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated. Next sign-in uses the new password.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password.");
    } finally { setPwBusy(false); }
  };

  const addAdvisor = async (e) => {
    e.preventDefault();
    const uid = newAdvisor.user_id.trim().toLowerCase();
    if (!uid || !newAdvisor.full_name.trim()) { toast.error("User ID and full name are required."); return; }
    try {
      await api.post("/advisors", { user_id: uid, full_name: newAdvisor.full_name.trim() });
      toast.success(`Created advisor "${uid}". They can now request an OTP.`);
      setNewAdvisor({ user_id: "", full_name: "" });
      loadAdvisors();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create advisor.");
    }
  };

  const removeAdvisor = async (uid) => {
    if (!window.confirm(`Delete advisor "${uid}"? All their trusted devices will be removed.`)) return;
    try {
      await api.delete(`/advisors/${uid}`);
      toast.success("Advisor removed.");
      loadAdvisors();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  const resetAdvisor = async (uid) => {
    if (!window.confirm(`Reset PIN + trusted devices for "${uid}"?`)) return;
    try {
      await api.post(`/advisors/${uid}/reset`);
      toast.success("PIN cleared. They'll set a new one after next OTP.");
      loadAdvisors();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed."); }
  };

  const revokeDevice = async (uid, deviceId) => {
    try {
      await api.delete(`/advisors/${uid}/devices/${deviceId}`);
      toast.success("Device access revoked.");
      loadAdvisors();
    } catch { toast.error("Revoke failed."); }
  };

  const copyOtp = () => {
    if (!pendingOtp?.code) return;
    try {
      navigator.clipboard.writeText(pendingOtp.code);
      toast.success("OTP copied. Share it with the advisor.");
    } catch { toast.error("Copy failed."); }
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8">
        <p className="font-mono-brand text-propertic-gold mb-2">Settings</p>
        <h1 className="font-editorial text-4xl text-propertic-navy">Access & security</h1>
        <p className="text-propertic-muted text-sm mt-2 max-w-2xl">Change your CRM password, create advisor logins and manage their trusted devices.</p>
      </div>

      {isOwner && pendingOtp && (
        <div className="mb-8 rounded-2xl border border-propertic-gold/40 bg-propertic-sand/60 p-5 flex flex-wrap items-center gap-4" data-testid="pending-otp-card">
          <ShieldCheck className="w-5 h-5 text-propertic-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-gold mb-0.5">Pending advisor OTP</p>
            <p className="text-sm text-propertic-navy">
              <span className="font-editorial text-lg">{pendingOtp.advisor_label}</span>
              <span className="text-propertic-muted"> · requested {new Date(pendingOtp.created_at).toLocaleTimeString()}</span>
            </p>
          </div>
          <div className="inline-flex items-center gap-2 bg-white border hairline rounded-xl px-4 h-11 font-editorial text-2xl tracking-[0.35em] tabular text-propertic-navy" data-testid="pending-otp-code">{pendingOtp.code}</div>
          <button onClick={copyOtp} data-testid="copy-otp-btn" className="h-11 px-4 rounded-full bg-propertic-navy text-propertic-paper text-[11px] font-mono-brand tracking-[0.14em] uppercase inline-flex items-center gap-2 hover:bg-propertic-navy-2">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-2xl border hairline p-6" data-testid="change-password-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-propertic-sand grid place-items-center text-propertic-gold"><KeyRound className="w-4 h-4" /></div>
            <div>
              <h2 className="font-editorial text-xl text-propertic-navy">Change CRM password</h2>
              <p className="text-[11px] text-propertic-muted">Owner-only. Sign out required after change.</p>
            </div>
          </div>
          {isOwner ? (
            <form onSubmit={changePassword} className="space-y-4">
              <PwField label="Current password" value={pw.current} onChange={(v) => setPw((p) => ({ ...p, current: v }))} testid="pw-current" />
              <PwField label="New password (min 8 chars)" value={pw.next} onChange={(v) => setPw((p) => ({ ...p, next: v }))} testid="pw-new" />
              <PwField label="Confirm new password" value={pw.confirm} onChange={(v) => setPw((p) => ({ ...p, confirm: v }))} testid="pw-confirm" />
              <button type="submit" disabled={pwBusy} data-testid="pw-submit" className="btn-gold rounded-full h-11 px-6 text-xs font-mono-brand tracking-widest disabled:opacity-60">{pwBusy ? "…" : "Update password"}</button>
            </form>
          ) : (
            <p className="text-sm text-propertic-muted">Only the owner can change the master CRM password.</p>
          )}
        </section>

        <section className="bg-white rounded-2xl border hairline p-6" data-testid="new-advisor-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-propertic-sand grid place-items-center text-propertic-gold"><UserPlus className="w-4 h-4" /></div>
            <div>
              <h2 className="font-editorial text-xl text-propertic-navy">Add an advisor</h2>
              <p className="text-[11px] text-propertic-muted">Give them a User ID. They'll request an OTP on first login.</p>
            </div>
          </div>
          {isOwner ? (
            <form onSubmit={addAdvisor} className="space-y-4">
              <label className="block">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">User ID (lowercase, no spaces)</span>
                <input value={newAdvisor.user_id} onChange={(e) => setNewAdvisor((f) => ({ ...f, user_id: e.target.value }))} placeholder="e.g. ahmed" data-testid="new-advisor-userid" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none tabular" />
              </label>
              <label className="block">
                <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">Full name</span>
                <input value={newAdvisor.full_name} onChange={(e) => setNewAdvisor((f) => ({ ...f, full_name: e.target.value }))} placeholder="Ahmed Al Mansouri" data-testid="new-advisor-fullname" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none" />
              </label>
              <button type="submit" data-testid="new-advisor-submit" className="btn-gold rounded-full h-11 px-6 text-xs font-mono-brand tracking-widest">Create advisor</button>
            </form>
          ) : (
            <p className="text-sm text-propertic-muted">Only the owner can add advisors.</p>
          )}
        </section>
      </div>

      {isOwner && (
        <section className="mt-8 bg-white rounded-2xl border hairline p-6" data-testid="advisor-list-card">
          <h2 className="font-editorial text-xl text-propertic-navy mb-5">Advisors & trusted devices</h2>
          {loading ? (
            <p className="text-sm text-propertic-muted">Loading…</p>
          ) : advisors.length === 0 ? (
            <p className="text-sm text-propertic-muted">No advisors yet. Add one above.</p>
          ) : (
            <div className="space-y-4">
              {advisors.map((a) => (
                <div key={a.user_id} className="border hairline rounded-xl p-4" data-testid={`advisor-row-${a.user_id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-editorial text-lg text-propertic-navy">{a.full_name}</p>
                      <p className="text-[12px] text-propertic-muted">
                        <span className="font-mono-brand text-propertic-gold">@{a.user_id}</span>
                        <span> · {a.pin_set ? "PIN set" : "PIN not set yet"}</span>
                        <span> · {a.devices?.length || 0} device(s)</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => resetAdvisor(a.user_id)} data-testid={`advisor-reset-${a.user_id}`} title="Clear PIN + trusted devices" className="h-9 px-3 rounded-full border hairline text-[10px] font-mono-brand tracking-[0.14em] uppercase text-propertic-navy hover:border-propertic-gold hover:text-propertic-gold inline-flex items-center gap-1.5"><RotateCcw className="w-3 h-3" /> Reset</button>
                      <button onClick={() => removeAdvisor(a.user_id)} data-testid={`advisor-delete-${a.user_id}`} title="Delete advisor" className="h-9 px-3 rounded-full border border-red-200 text-[10px] font-mono-brand tracking-[0.14em] uppercase text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"><Trash2 className="w-3 h-3" /> Remove</button>
                    </div>
                  </div>
                  {a.devices?.length > 0 && (
                    <div className="mt-4 grid sm:grid-cols-2 gap-2">
                      {a.devices.map((d) => (
                        <div key={d.device_id} className="border hairline rounded-lg p-3 flex items-start justify-between gap-3 bg-propertic-cream/40">
                          <div className="min-w-0">
                            <p className="text-[13px] text-propertic-navy inline-flex items-center gap-1.5"><MonitorSmartphone className="w-3.5 h-3.5 text-propertic-gold" /> {d.label}</p>
                            <p className="text-[10px] text-propertic-muted mt-0.5 font-mono-brand inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> {d.last_login_at ? new Date(d.last_login_at).toLocaleString() : "never"}</p>
                          </div>
                          <button onClick={() => revokeDevice(a.user_id, d.device_id)} data-testid={`revoke-device-${d.device_id}`} className="text-[10px] font-mono-brand tracking-[0.14em] uppercase text-red-600 hover:underline">Revoke</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      {isOwner && (
        <section className="mt-8 rounded-2xl border-2 border-red-200 bg-red-50/40 p-6" data-testid="danger-zone-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-red-100 grid place-items-center text-red-600"><AlertTriangle className="w-4 h-4" /></div>
            <div>
              <h2 className="font-editorial text-xl text-red-800">Danger zone</h2>
              <p className="text-[11px] text-red-700/70">Owner-only. Use these once, at the end of the test phase, to wipe demo data before going live.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <PurgeButton
              testid="purge-leads-btn"
              icon={Database}
              title="Delete ALL leads"
              subtitle="Removes every lead + CRM event. Advisors and PIN devices are kept."
              confirmPrompt='This will permanently delete EVERY lead in the pipeline. Type "delete leads" to confirm.'
              confirmWord="delete leads"
              endpoint="/admin/purge/leads"
              successMsg={(d) => `Deleted ${d.deleted_leads} leads.`}
            />
            <PurgeButton
              testid="purge-analytics-btn"
              icon={Eraser}
              title="Clear ALL analytics"
              subtitle="Removes pageviews and WhatsApp/Call click logs. Fresh counters."
              confirmPrompt='This will permanently clear all site analytics (visitors + click log). Type "clear analytics" to confirm.'
              confirmWord="clear analytics"
              endpoint="/admin/purge/analytics"
              successMsg={(d) => `Cleared ${d.deleted_daily} daily rollups, ${d.deleted_events} pageviews, ${d.deleted_clicks} click events.`}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function PurgeButton({ testid, icon: Icon, title, subtitle, confirmPrompt, confirmWord, endpoint, successMsg }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const ans = window.prompt(confirmPrompt);
    if (!ans || ans.trim().toLowerCase() !== confirmWord) {
      if (ans !== null) toast.error("Confirmation phrase did not match. Nothing deleted.");
      return;
    }
    if (!window.confirm(`FINAL WARNING, ${title.toLowerCase()}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post(endpoint, {});
      toast.success(successMsg(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed.");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid={testid}
      className="text-left rounded-xl border border-red-200 bg-white p-4 hover:border-red-500 hover:bg-red-50 transition-colors disabled:opacity-60 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-lg bg-red-100 grid place-items-center text-red-600 shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="font-editorial text-[15px] text-red-800 leading-tight">{title}</p>
        <p className="text-[11px] text-red-700/70 mt-1">{subtitle}</p>
        {busy && <p className="text-[10px] text-red-600 mt-1 font-mono-brand">Deleting…</p>}
      </div>
    </button>
  );
}

function PwField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">{label}</span>
      <input type="password" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none" />
    </label>
  );
}
